import React from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { AlertCircle, Loader2, MapPin } from 'lucide-react';
import { COUNTRIES, LANGUAGES, US_STATES } from '../../data/createClubData';

export default function Step3Location({ formData, setFormData, existingClubs, showExistingClubs, setShowExistingClubs, checkingExisting }) {
  return (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  Club Location
                </CardTitle>
                <CardDescription>Where is your club based? This helps members find local communities.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="country">Country *</Label>
                  <select
                    id="country"
                    value={formData.country}
                    onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value, state: '' }))}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                    data-testid="country-select"
                  >
                    {COUNTRIES.map(c => (
                      <option key={c.code} value={c.code}>{c.name}</option>
                    ))}
                  </select>
                </div>
                
                {formData.country === 'US' && (
                  <div>
                    <Label htmlFor="state">State</Label>
                    <select
                      id="state"
                      value={formData.state}
                      onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
                      className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                      data-testid="state-select"
                    >
                      <option value="">Select a state (optional)</option>
                      {US_STATES.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                )}
                
                <div>
                  <Label htmlFor="region">Region / Area Name</Label>
                  <Input
                    id="region"
                    value={formData.region}
                    onChange={(e) => setFormData(prev => ({ ...prev, region: e.target.value }))}
                    placeholder="e.g., Pacific Northwest, Southern California, Greater London..."
                    data-testid="region-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Optional - helps describe your club's geographic focus</p>
                </div>
                
                <div>
                  <Label htmlFor="language">Preferred Language</Label>
                  <select
                    id="language"
                    value={formData.default_language}
                    onChange={(e) => setFormData(prev => ({ ...prev, default_language: e.target.value }))}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                    data-testid="language-select"
                  >
                    {LANGUAGES.map(l => (
                      <option key={l.code} value={l.code}>{l.name}</option>
                    ))}
                  </select>
                  <p className="text-xs text-muted-foreground mt-1">The primary language for club communications</p>
                </div>
              </CardContent>
            </Card>

            {/* Existing Clubs Alert */}
            {showExistingClubs && existingClubs.length > 0 && (
              <Card className="border-amber-200 bg-amber-50/50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-amber-800">
                    <AlertCircle className="w-5 h-5" />
                    Similar Clubs Found
                  </CardTitle>
                  <CardDescription>
                    There are already clubs for similar pet types in your region. You might want to join one instead!
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {existingClubs.map(club => (
                      <div 
                        key={club.id} 
                        className="flex items-center justify-between p-3 bg-white rounded-lg border"
                      >
                        <div>
                          <div className="font-medium">{club.name}</div>
                          <div className="text-sm text-muted-foreground">
                            {club.pet_types?.join(', ')} • {club.region || club.state || club.country}
                          </div>
                          {club.motto && (
                            <div className="text-xs text-muted-foreground italic">"{club.motto}"</div>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Badge variant="outline">{club.member_count || 0} members</Badge>
                          <Button size="sm" variant="outline" asChild>
                            <Link to={`/clubs/${club.slug}`}>View</Link>
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-amber-700 mt-4">
                    It's okay to create a new club even if similar ones exist - multiple communities can thrive!
                  </p>
                  <Button 
                    variant="outline" 
                    className="mt-2"
                    onClick={() => setShowExistingClubs(false)}
                  >
                    Continue Creating My Club
                  </Button>
                </CardContent>
              </Card>
            )}
            
            {checkingExisting && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                Checking for similar clubs...
              </div>
            )}
          </div>
  );
}
